import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { api } from '../api/client';
import { usePageTitle } from '../hooks/usePageTitle';

export function PSQForm() {
  usePageTitle('Pre-Session Questionnaire');
  const { id, sessionId } = useParams<{ id: string; sessionId: string }>();
  const navigate = useNavigate();
  const [energyScore, setEnergyScore] = useState(5);
  const [microActionReview, setMicroActionReview] = useState('');
  const [biggestInsight, setBiggestInsight] = useState('');
  const [sessionFocus, setSessionFocus] = useState('');
  const [anythingElse, setAnythingElse] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      await api.post(`/programmes/${id}/sessions/${sessionId}/psq`, {
        energy_score: energyScore,
        micro_action_review: microActionReview,
        biggest_insight: biggestInsight,
        session_focus: sessionFocus,
        anything_else: anythingElse || null,
      });
      navigate(`/programme/${id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto mt-10 px-6 pb-20">
        <h1 className="font-display text-3xl font-bold text-mustard-green-dark mb-2">
          Pre-Session Questionnaire
        </h1>
        <p className="text-mustard-charcoal/60 mb-8">
          Complete this 24-48 hours before your session
        </p>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Energy Score */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <label className="block text-sm font-medium text-mustard-charcoal mb-3">
              How is your energy right now on a scale of 1 to 10?
            </label>
            <div className="flex items-center gap-3">
              <input
                type="range"
                min={1}
                max={10}
                value={energyScore}
                onChange={(e) => setEnergyScore(Number(e.target.value))}
                className="flex-1"
              />
              <span className="text-2xl font-bold text-mustard-green-dark w-8 text-center">
                {energyScore}
              </span>
            </div>
          </div>

          {/* Micro-action review */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <label className="block text-sm font-medium text-mustard-charcoal mb-2">
              How did you get on with your micro-actions since the last session?
            </label>
            <textarea
              value={microActionReview}
              onChange={(e) => setMicroActionReview(e.target.value)}
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mustard-green focus:border-transparent outline-none text-sm"
            />
          </div>

          {/* Biggest insight */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <label className="block text-sm font-medium text-mustard-charcoal mb-2">
              What is the biggest insight or shift from the last session?
            </label>
            <textarea
              value={biggestInsight}
              onChange={(e) => setBiggestInsight(e.target.value)}
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mustard-green focus:border-transparent outline-none text-sm"
            />
          </div>

          {/* Session focus */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <label className="block text-sm font-medium text-mustard-charcoal mb-2">
              What do you most want to get from today's session?
            </label>
            <textarea
              value={sessionFocus}
              onChange={(e) => setSessionFocus(e.target.value)}
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mustard-green focus:border-transparent outline-none text-sm"
            />
          </div>

          {/* Anything else */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <label className="block text-sm font-medium text-mustard-charcoal mb-2">
              Is there anything else you want your coach to know before we start? <span className="text-mustard-charcoal/40">(Optional)</span>
            </label>
            <textarea
              value={anythingElse}
              onChange={(e) => setAnythingElse(e.target.value)}
              rows={2}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mustard-green focus:border-transparent outline-none text-sm"
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-mustard-green-dark text-white py-3 rounded-lg font-medium hover:bg-mustard-green transition-colors disabled:opacity-50"
          >
            {submitting ? 'Submitting...' : 'Submit Questionnaire'}
          </button>
        </form>
      </div>
    </Layout>
  );
}
